import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  InputSignal,
  OutputEmitterRef,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { map } from 'rxjs/operators';

import { NotificationService, filterUndefined } from '@core/shared/util';
import { AssignedOrganization, OrganizationSelectorComponent, OrganizationSelectorFlyoutFacade } from '@core/ui';
import {
  CreatePlatformCatalog,
  FlyoutIndustryCatalog,
  IndustryCatalogAssignment,
  PlatformCatalogAssignedOrganization,
  PlatformCatalogAvailableOrganization,
} from '@mp/content-manager/plattformkataloge/data-access';

import { ActionsSlideToggleButtonComponent } from '../actions-slide-toggle-button/actions-slide-toggle-button.component';
import { PlattformkatalogeFlyoutFacade } from '../facades/plattformkataloge-flyout.facade';
import { PlattformkatalogeFacade } from '../facades/plattformkataloge.facade';
import { IndustryCatalogsContainerComponent } from '../industriekataloge-list/industry-catalogs-container.component';
import { SortableCatalog } from '../industriekataloge-list/sortable-katalog-list/models';
import {
  PlatformCatalogCountInfo,
  PlatformCatalogPanelComponent,
  PlatformCatalogPanelTab,
} from '../plattformkataloge-list/platformcatalog-panel/platformcatalog-panel.component';
import { flyoutIndustriekatalogToSortierbarerKatalog } from '../plattformkataloge-utils';

@Component({
  selector: 'mp-plattformkatalog-create',
  templateUrl: './plattformkatalog-create.component.html',
  styleUrl: './plattformkatalog-create.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IndustryCatalogsContainerComponent,
    OrganizationSelectorComponent,
    PlatformCatalogPanelComponent,
    ActionsSlideToggleButtonComponent,
  ],
})
export class PlattformkatalogCreateComponent {
  readonly nameControl: InputSignal<FormControl<string> | null | undefined> = input<
    FormControl<string> | null | undefined
  >();

  readonly created: OutputEmitterRef<CreatePlatformCatalog> = output<CreatePlatformCatalog>();
  readonly cancelled: OutputEmitterRef<void> = output<void>();

  protected isSearchMultipleDataSourcesActive = false;

  protected kataloge: Array<IndustryCatalogAssignment> = [];
  protected searchTerm = '';
  protected articleCount = 0;

  protected activeTab: PlatformCatalogPanelTab = 'catalogs';

  private readonly _organizations = new Map<string, PlatformCatalogAssignedOrganization>();

  constructor(
    private flyoutFacade: PlattformkatalogeFlyoutFacade,
    private toaster: NotificationService,
    private readonly plattformKatalogeFacade: PlattformkatalogeFacade,
    private readonly organizationSelectorFlyoutFacade: OrganizationSelectorFlyoutFacade,
    private readonly cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  protected get countInfo(): PlatformCatalogCountInfo {
    return {
      industryCatalogs: this.kataloge.length,
      organizations: this._organizations.size,
      articles: this.articleCount,
    };
  }

  protected get organizations() {
    return Array.from(this._organizations.values());
  }

  protected onEditModeChange(editMode: boolean): void {
    if (!editMode) {
      this.cancelled.emit();
    }
  }

  emitSave(name: string): void {
    const industryCatalogs = this.kataloge.map(({ catalogId, order }) => ({ catalogId, order }));
    this.created.emit({
      name,
      industryCatalogs,
      isSearchMultipleDataSourcesActive: this.isSearchMultipleDataSourcesActive,
      assignedOrganizationIds: Array.from(this._organizations.keys()),
    });
  }

  handleItemRemoved(katalog: IndustryCatalogAssignment): void {
    const matchIndex = this.kataloge.findIndex(({ catalogId }) => catalogId === katalog.catalogId);

    if (matchIndex === -1) {
      throw new Error(
        `Katalog mit ID ${katalog.catalogId} konnte nicht gefunden werden. Das hätte nicht passieren dürfen!`,
      );
    }

    this.kataloge = [
      ...this.kataloge.slice(0, matchIndex),
      ...this.kataloge.slice(matchIndex + 1, this.kataloge.length),
    ];
    this.articleCount -= katalog.articleCount.total;
  }

  handleItemsAdded(kataloge: Array<IndustryCatalogAssignment>): void {
    this.promptSuccessToast(kataloge);
    this.kataloge = [...this.kataloge, ...kataloge];
    this.articleCount += kataloge.reduce((count, catalog) => count + catalog.articleCount.total, 0);
  }

  private promptSuccessToast(katalogIds: Array<unknown>): void {
    const multipleAdded = katalogIds.length > 1;
    const message = multipleAdded
      ? `${katalogIds.length} Industriekataloge erfolgreich hinzugefügt!`
      : 'Industriekatalog erfolgreich hinzugefügt!';

    this.toaster.toastSuccess(message);
  }

  handleItemSorted({ previousIndex, currentIndex }: { id: string; previousIndex: number; currentIndex: number }): void {
    moveItemInArray(this.kataloge, previousIndex, currentIndex);
    const katalogeWithUpdatedReihenfolge = this.kataloge.map((katalog, index) => ({
      ...katalog,
      reihenfolge: index + 1,
    }));
    this.kataloge = [...katalogeWithUpdatedReihenfolge];
  }

  handleSearched(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.kataloge = [...this.kataloge];
  }

  navigateToIndustryCatalogDetail(catalogId: string): void {
    this.plattformKatalogeFacade.navigateToIndustryCatalogDetail(catalogId);
  }

  letUserAddCatalogs(resolver: (kataloge: Array<SortableCatalog>) => void): void {
    const alreadyAttachedCatalogIds: Array<string> = this.kataloge.map(({ catalogId }) => catalogId);
    const flyoutRef = this.flyoutFacade.promptKatalogSelectionFlyout({ excludeIds: alreadyAttachedCatalogIds });

    if (!flyoutRef) return;

    function convertKataloge(kataloge: Array<FlyoutIndustryCatalog> | undefined): Array<SortableCatalog> | undefined {
      return kataloge?.map((katalog, index) => {
        const reihenfolge = alreadyAttachedCatalogIds.length + index + 1;
        return flyoutIndustriekatalogToSortierbarerKatalog(katalog, reihenfolge);
      });
    }

    flyoutRef.afterClosed$.pipe(map(convertKataloge), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (kataloge) => {
        if (kataloge !== undefined) {
          resolver(kataloge);
        }
      },
    });
  }

  protected toggleIsSearchMultipleDataSourcesActive() {
    this.isSearchMultipleDataSourcesActive = !this.isSearchMultipleDataSourcesActive;
  }

  // The following methods are to manage platformcatalog organizations (mp-selector):
  protected letUserAddOrganizations(): void {
    // alreadyAttachedOrganizations tells the facade which options already been selected and should not be an option to select
    const alreadyAttachedOrganizations = Array.from(this._organizations.keys());
    const organisationSelectionItems$ = this.organizationSelectorFlyoutFacade.buildOrganisationSelectionItems$(
      this.plattformKatalogeFacade.availableOrganizations$,
      alreadyAttachedOrganizations,
    );

    this.organizationSelectorFlyoutFacade
      .promptOrganisationFlyout(organisationSelectionItems$)
      ?.afterClosed$.pipe(filterUndefined(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (userSelection) => {
          this.addOrganizations(userSelection);
        },
      });
  }

  private addOrganizations(selection: PlatformCatalogAvailableOrganization[]): void {
    for (const org of selection) {
      if (this._organizations.has(org.organizationId)) {
        continue;
      }

      this._organizations.set(org.organizationId, {
        ...org,
        isRemoved: false,
      });
    }

    this.cdr.detectChanges();
  }

  protected removeToggleOrganization(organisation: AssignedOrganization) {
    this._organizations.delete(organisation.organizationId);
  }
}
